import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';

/**
 * Interval for attempting silent token refresh (5 minutes).
 * Keeps the token fresh so it doesn't expire during an active session.
 */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Custom React hook that periodically refreshes the access token silently.
 *
 * Behavior:
 * - Every 5 minutes, calls `acquireTokenSilent` to refresh the token from cache or renew it
 * - If silent refresh fails due to interaction required (e.g., consent, MFA),
 *   triggers a login redirect so the user can re-authenticate
 * - Handles other errors gracefully (logs them but does not interrupt the session)
 */
export function useTokenRefresh() {
  const { instance, accounts } = useMsal();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const refreshToken = async () => {
      const activeAccount = instance.getActiveAccount() ?? accounts[0];
      if (!activeAccount) {
        return;
      }

      try {
        await instance.acquireTokenSilent({
          ...loginRequest,
          account: activeAccount,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Silent refresh failed — user interaction is required
          instance.loginRedirect(loginRequest);
        } else {
          // Log other errors but don't interrupt the session
          console.warn('Silent token refresh failed:', error);
        }
      }
    };

    // Perform an initial refresh attempt
    refreshToken();

    // Schedule periodic refresh
    intervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [instance, accounts]);
}
